import { Box, Button, FormControl, InputLabel, LinearProgress, MenuItem, Select, SelectChangeEvent, TextField, Typography } from '@mui/material';
import ThemedJsonView from '../ThemedJsonView';
import React, { useEffect, useRef, useState } from 'react';
import { useDashboardContext } from '../../../components/providers/DashboardProvider';
import { useControlSocketContext } from '../../../components/providers/ControlSocketProvider';
import ConnectionIndicator from './ConnectionIndicator';

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SendIcon from '@mui/icons-material/Send';
import TerminalIcon from '@mui/icons-material/Terminal';

const TerminalPage: React.FC = () => {

  interface ConnectionOption {
    block_id: string;
    block_name: string;
    block_type: string;
    computer_id: string;
  }
  const {
    connected: connectedToSocket,
    waiting: waitingResponse,
    connect: connectControl,
    disconnect: disconnectControl,
    send: sendControlMessage,
    components: componentOptions,
    request,
    response,
    connectionOptions,
    clear,
  } = useControlSocketContext();


  const [buttonEnabled, setButtonEnabled] = useState<boolean>(false);

  const [pendingMessage, setPendingMessage] = useState<Record<string, unknown>>({});

  const [selectedConnection, setSelectedConnection] = useState<string>("");

  const [selectedComponent, setSelectedComponent] = useState<string>("");
  const [customComponent, setCustomComponent] = useState<string>("");

  const [commandOptions, setCommandOptions] = useState<Array<string>>([]);
  const [selectedCommand, setSelectedCommand] = useState<string>("");
  const [customCommand, setCustomCommand] = useState<string>("");

  const [sendEnabled, setSendEnabled] = useState<boolean>(false);

  const [computerId, setComputerId] = useState<string>("");



  useEffect(() => {
    // fetchClusterComponentsTree();

    return () => {
      disconnectControl();
    };
  }, []);


  useEffect(() => {
    setSelectedCommand("");
    setSelectedComponent("");
  }, [connectedToSocket]);

  useEffect(() => {
    if (selectedComponent == "computer_monitor") {
      setCommandOptions(["ping"]);
    } else {
      setCommandOptions(["ping"]);
    }
  }, [selectedComponent]);

  useEffect(() => {
    let component = selectedComponent;
    let command = selectedCommand;

    if (component == "<<custom>>") {
      component = customComponent;
    }

    if (command == "<<custom>>") {
      command = customCommand;
    }

    let _message: Record<string, unknown>;
    _message = {
      "command": command
    }

    if (component != "") {
      _message = {
        "command": command,
        "component": component,
      }
    }

    if (component == "" && command == "") {
      _message = {};
    }

    // console.log(_message) {
    setPendingMessage(_message);

    if (command == "") {
      setSendEnabled(false);
    } else {
      setSendEnabled(true);
    }

  }, [selectedCommand, selectedComponent, customComponent, customCommand])


  function toggleConnection(): void {
    if (connectedToSocket) {
      // disconnect();
      disconnectControl();
    } else {
      // connect();
      connectControl(computerId);
    }
  }

  function handleConnectionSelected(event: SelectChangeEvent) {
    if (event.target.value) {
      setComputerId(event.target.value);
      setSelectedConnection(event.target.value);
    }
  }

  function handleComponentSelected(event: SelectChangeEvent) {
    // console.log("Select:", event.target.value);

    if (event.target.value == "") {
      setSelectedComponent("");
    } else {
      setSelectedComponent(event.target.value);
    }
  }

  function handleCommandSelected(event: SelectChangeEvent) {
    // console.log("Select:", event.target.value);
    if (event.target.value == "") {
      setSelectedCommand("");
    } else {
      setSelectedCommand(event.target.value);
    }
  }


  const cardSx = {
    backgroundColor: "var(--c-surface)",
    border: "1px solid var(--c-border)",
    borderRadius: "16px",
    p: 2.5,
    minWidth: 0,
  };

  const cardTitleSx = { fontSize: 15, fontWeight: 600, color: "var(--c-text)", lineHeight: 1.3 };
  const sectionLabelSx = { fontSize: 12, fontWeight: 600, color: "var(--c-text-muted)", letterSpacing: "0.04em", textTransform: "uppercase" };

  // Monospace panel used for the message preview, request and response
  const codePanelSx = {
    width: "100%",
    boxSizing: "border-box",
    border: "1px solid var(--c-border)",
    borderRadius: "10px",
    backgroundColor: "var(--c-subtle)",
    p: 1.5,
    overflow: "auto",
    "& .w-json-view-container": { backgroundColor: "transparent !important", fontSize: 13 },
  };

  const inputSx = { "& .MuiOutlinedInput-root": { borderRadius: "10px" } };

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
      pt: 2,
    }}>

      {/* Connection card */}
      <Box sx={{ ...cardSx, display: "flex", flexDirection: "column", gap: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
          <Box>
            <Typography sx={cardTitleSx}>Connection</Typography>
            <Typography sx={{ fontSize: 13, color: "var(--c-text-muted)" }}>
              Choose a block and connect to its site computer
            </Typography>
          </Box>
          <ConnectionIndicator connected={connectedToSocket} />
        </Box>

        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5 }}>
          <FormControl size="small" sx={{ ...inputSx, minWidth: 300, flex: { xs: 1, sm: "0 0 auto" } }}>
            <InputLabel id="block-label">Block</InputLabel>
            <Select
              disabled={connectedToSocket}
              labelId="block-label"
              label="Block"
              onChange={handleConnectionSelected}
              value={selectedConnection}
            >
              {
                connectionOptions.map((option: ConnectionOption, index: number) => {
                  return (
                    <MenuItem
                      key={index}
                      value={option.computer_id}
                    >
                      {option.block_name}
                    </MenuItem>
                  );
                })
              }
            </Select>
          </FormControl>

          <Button
            variant={!connectedToSocket ? "contained" : "outlined"}
            color={connectedToSocket ? "error" : "primary"}
            onClick={toggleConnection}
            disabled={!connectedToSocket && !selectedConnection}
            loading={waitingResponse}
            disableElevation
            sx={{
              minWidth: 140,
              height: 40,
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            {connectedToSocket ? 'Disconnect' : 'Connect'}
          </Button>
        </Box>
      </Box>

      {!connectedToSocket &&
        <Box sx={{
          ...cardSx,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1,
          py: 6,
          color: "var(--c-text-muted)",
          textAlign: "center",
        }}>
          <TerminalIcon sx={{ fontSize: 40, color: "var(--c-border-strong)" }} />
          <Typography sx={{ fontSize: 15, fontWeight: 600, color: "var(--c-text-2)" }}>
            Not connected
          </Typography>
          <Typography sx={{ fontSize: 13 }}>
            Connect to a block to compose and send commands.
          </Typography>
        </Box>
      }

      {(connectedToSocket) &&

        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 2fr) minmax(0, 3fr)" },
          alignItems: 'start',
          gap: 2,
        }}>

          {/* Command */}
          <Box sx={{ ...cardSx, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography sx={cardTitleSx}>Compose command</Typography>

            <FormControl fullWidth size="small" sx={inputSx}>
              <InputLabel id="command-label">Command</InputLabel>
              <Select
                disabled={!connectedToSocket}
                labelId="command-label"
                label="Command"
                onChange={handleCommandSelected}
                value={selectedCommand}
              >
                <MenuItem
                  key={0}
                  value=""
                >
                  <em>None</em>
                </MenuItem>
                <MenuItem
                  key={1}
                  value="<<custom>>"
                >
                  <em>Custom</em>
                </MenuItem>
                {
                  commandOptions.map((option: string, index: number) => {
                    return (
                      <MenuItem
                        key={index + 2}
                        value={option}
                      >
                        {option}
                      </MenuItem>
                    );
                  })
                }
              </Select>
            </FormControl>

            {
              (selectedCommand == "<<custom>>") &&
              <TextField
                disabled={!connectedToSocket}
                fullWidth
                size="small"
                label="Custom command"
                sx={inputSx}
                onChange={(event) => {
                  setCustomCommand(event.target.value);
                }}
              />
            }

            <FormControl fullWidth size="small" sx={inputSx}>
              <InputLabel id="component-label">Component</InputLabel>
              <Select
                disabled={!connectedToSocket}
                labelId="component-label"
                label="Component"
                onChange={handleComponentSelected}
                value={selectedComponent}
              >
                <MenuItem
                  key={0}
                  value=""
                >
                  <em>None</em>
                </MenuItem>
                <MenuItem
                  key={1}
                  value="<<custom>>"
                >
                  <em>Custom</em>
                </MenuItem>
                {
                  componentOptions?.map((option: string, index: number) => {
                    return (
                      <MenuItem
                        key={index + 2}
                        value={option}
                      >
                        {option}
                      </MenuItem>
                    );
                  })
                }
              </Select>
            </FormControl>

            {
              selectedComponent == "<<custom>>" &&
              <TextField
                disabled={!connectedToSocket}
                fullWidth
                size="small"
                label="Custom component"
                sx={inputSx}
                onChange={(event) => {
                  setCustomComponent(event.target.value);
                }}
              />
            }

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography sx={sectionLabelSx}>Message preview</Typography>
              <Box sx={{ ...codePanelSx, minHeight: 120 }}>
                <ThemedJsonView
                  value={pendingMessage}
                  displayDataTypes={false}
                />
              </Box>
            </Box>

            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant={"contained"}
                onClick={() => sendControlMessage(pendingMessage)}
                disabled={!sendEnabled}
                disableElevation
                startIcon={<SendIcon />}
                sx={{ flex: 1, height: 40, borderRadius: "10px", textTransform: "none", fontWeight: 600 }}
              >
                Send
              </Button>

              <Button
                variant={"outlined"}
                onClick={() => clear()}
                startIcon={<DeleteOutlineIcon />}
                sx={{
                  height: 40,
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 600,
                  borderColor: "var(--c-border)",
                  color: "var(--c-text-2)",
                }}
              >
                Clear
              </Button>
            </Box>
          </Box>

          {/* Communication */}
          <Box sx={{ ...cardSx, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography sx={cardTitleSx}>Communication</Typography>

            {/* Request */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography sx={sectionLabelSx}>Request</Typography>
              <Box sx={{ ...codePanelSx, minHeight: 80 }}>
                <ThemedJsonView
                  value={request}
                  displayDataTypes={false}
                />
              </Box>
            </Box>

            {/* Response */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography sx={sectionLabelSx}>Response</Typography>
              <Box sx={{ ...codePanelSx, minHeight: 300, position: "relative", pt: waitingResponse ? 2.5 : 1.5 }}>
                {waitingResponse &&
                  <LinearProgress
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      borderTopLeftRadius: "10px",
                      borderTopRightRadius: "10px",
                    }}
                  />
                }

                <ThemedJsonView
                  value={response}
                  displayDataTypes={false}
                />
              </Box>
            </Box>
          </Box>

        </Box>

      }

    </Box>

  );
};

export default TerminalPage;
